import { useEffect, useState } from 'react'
import { hasConsent, subscribeConsent, type ConsentCategory } from '@/lib/consent'

/**
 * SAB-938 — the ONE place that decides whether the Freshdesk web chat widget exists.
 *
 * ── WHY THIS MODULE EXISTS ─────────────────────────────────────────────────
 *
 * The widget used to load from an inline script in index.html bound to `window.load`, which
 * meant it ran unconditionally — before the visitor had answered the cookie banner. Measured
 * on the real app: 29 requests to Freshdesk/Freshworks/AWS completed pre-consent, a visitor
 * record was created, and a DURABLE identifier (`fdWcToken`/`fdWcAlias`) was written to
 * localStorage on the freshdesk.com origin, stable across reloads.
 *
 * The vendor toggle ("Stop tracking user events") removed the per-page `page_visit` trail —
 * one request of the 29. Everything else was the widget bootstrapping itself, which no
 * vendor setting turns off. Hence gating the LOAD.
 *
 * ── WHY IT IS A MODULE AND NOT AN INLINE SCRIPT ────────────────────────────
 *
 * SAB-431's `subscribeConsent()` is an in-process pub/sub over a module-scoped Set — NOT a
 * DOM CustomEvent. An inline script in index.html cannot subscribe to it, so it could never
 * react to an accept in the same session; the visitor would have to reload before support
 * chat appeared. The only alternative was re-reading `sf_consent_v1` from a plain script,
 * which is a second consent mechanism in miniature and drifts from SAB-431 the first time
 * the storage shape changes.
 *
 * So the decision lives here, in the same module instance as the consent store, and
 * index.html carries only the launcher's CSS placement rules.
 *
 * ⚠️ THE IDs LIVE HERE AND NOWHERE ELSE. `reInit()` takes a FULL config rather than a patch,
 * so FreshdeskIdentity has to pass token/host/widgetId back in. When those literals existed
 * in two places the failure mode was a widget initialising under one widgetId and
 * re-initialising under another — two conversation histories for one user, and no error
 * anywhere. One exported constant makes that impossible rather than merely unlikely.
 */

export const FD_CONFIG = {
  token: '01M19VAKP9JXJD4QY2WXAE07XS',
  host: 'https://sabotagefantasy.freshdesk.com',
  widgetId: '01M19VAPCGKYXQA9NZ90M32G7D',
} as const

const SCRIPT_ID = 'Freshdesk-js-sdk'
const SCRIPT_SRC = `${FD_CONFIG.host}/webchat/js/widget.js`

/**
 * ⚠️ THE GATE IS `analytics`, AND THAT IS A DELIBERATE PRODUCT DECISION, NOT A DEFAULT.
 *
 * Adam's call, with the consequence accepted explicitly: a visitor who opens Customize and
 * turns analytics OFF gets no live chat. Support chat is arguably functional rather than
 * analytics, but `analytics` is the only existing category that satisfies the requirement
 * that "declined" means the widget never loads — `rejectAll()` sets it false, and gating on
 * "any decision recorded" would load the widget for someone who declined.
 *
 * Nobody loses a route to support: every support control in the app is already a mailto to
 * support@sabotagefantasy.com, and /support is a public, non-gated route.
 */
export const WIDGET_CONSENT_CATEGORY: ConsentCategory = 'analytics'

export type FdWidget = {
  init?: (config: unknown) => void
  /**
   * SAB-943. READ OUT OF THE SHIPPED BUNDLE, not the documentation — the same method SAB-916
   * used to establish `fd_frame`. In `webchat/js/widget.js` the exported object carries:
   *
   *   open(e){ e ? x.subscribe("open_channel", e) : x.subscribe("open_chat") }
   *
   * and the vendor's own notification handler calls `window[u.widgetNamespace].open(...)`,
   * where `widgetNamespace` is "fdWidget" in the same config object as `frameDivId:"fd_frame"`.
   * So this is their supported entry point, corroborated by their usage.
   *
   * It posts a message INTO the widget iframe, whose handler we cannot inspect. Presence is
   * therefore not behaviour: call it only when `isLoaded()`, and assert the RESULT (a visible
   * chat frame) rather than that the method exists.
   */
  open?: (channelId?: string) => void
  reInit?: (config: unknown) => void
  authenticate?: (token: string) => void
  destroy?: () => void
  isLoaded?: () => boolean
  isInitialized?: () => boolean
  /** Whether the chat panel is currently open. Read from the bundle alongside open(). */
  isOpened?: () => boolean
  user?: { clear?: () => void }
}

declare global {
  interface Window { fdWidget?: FdWidget }
}

/** The single predicate. Everything else in this file defers to it. */
export function widgetAllowed(): boolean {
  return hasConsent(WIDGET_CONSENT_CATEGORY)
}

function initAnonymously() {
  // ANONYMOUS on purpose: jwtAuthToken is init-only and auth has not resolved at load time,
  // so a verified session cannot exist yet. FreshdeskIdentity upgrades it via reInit().
  if (window.fdWidget?.isInitialized?.()) return
  window.fdWidget?.init?.({ ...FD_CONFIG })
}

/**
 * Inject the vendor script and initialise. Re-checks consent itself rather than trusting its
 * caller — this is the last line before a third-party request, and a gate that only exists
 * one frame up is a gate that a future caller can forget.
 */
export function loadWidget(): void {
  if (typeof document === 'undefined' || !widgetAllowed()) return
  const existing = document.getElementById(SCRIPT_ID)
  if (existing) { initAnonymously(); return }
  const s = document.createElement('script')
  s.id = SCRIPT_ID
  s.async = true
  s.src = SCRIPT_SRC
  s.onload = initAnonymously
  document.head.appendChild(s)
}

/**
 * Best-effort teardown on withdrawal, and the limit is worth stating where it is done rather
 * than only in the policy: we can destroy the frame and stop every future request, but
 * `fdWcToken`/`fdWcAlias` live in localStorage on the freshdesk.com iframe origin, which is
 * cross-origin to us and unreachable from here, and the visitor record is server-side at
 * Freshworks. Withdrawal stops collection going forward. It does not erase what exists.
 */
export function destroyWidget(): void {
  if (typeof document === 'undefined') return
  try { window.fdWidget?.destroy?.() } catch (e) { console.error('[SAB-938] Freshdesk destroy() failed:', e) }
  document.getElementById('fd_frame')?.remove()
  // Drop the script too, so a later re-consent re-injects from a clean state rather than
  // depending on whatever the previous instance left on the global.
  document.getElementById(SCRIPT_ID)?.remove()
}

/**
 * SAB-943: open support chat on demand, from a control the room owns.
 *
 * Lives here rather than in the room because this module is already the single place that
 * knows the ids and the consent predicate. A component calling `window.fdWidget.open()`
 * directly would be a second opinion about when the widget may be touched, which is exactly
 * what the header of this file exists to prevent.
 *
 * Returns whether the call was made, so a caller can distinguish "opened" from "not
 * available" instead of assuming. Both guards are real states, not defensive noise:
 * `widgetAllowed()` is false whenever analytics consent is absent (SAB-938), and
 * `isLoaded()` is false in the window between consent and the vendor script finishing.
 */
export const SUPPORT_OPEN_CLASS = 'sf-support-open'

/** The vendor's frame container and its open marker, both read out of the shipped bundle. */
export const FRAME_ID = 'fd_frame'
export const FD_OPEN_CLASS = 'fd-open'

export function openSupport(): boolean {
  if (!widgetAllowed()) return false
  const w = window.fdWidget
  /**
   * NOT gated on `isLoaded()`. Measured in a browser: the chat opens correctly — the frame
   * goes 75px -> 844px and gains `fd-open` — while `isLoaded()` and `isOpened()` BOTH read
   * false. The vendor's state getters are not dependable in this build; the DOM is. So the
   * gate is the thing that must actually exist for the call to land: the frame the widget
   * created, and the method itself.
   */
  if (typeof w?.open !== 'function' || !document.getElementById(FRAME_ID)) return false
  try {
    /**
     * ⚠️ `#fd_frame` IS THE CHAT, NOT JUST THE LAUNCHER.
     *
     * SAB-942 hides `#fd_frame` inside the draft room because the launcher overlays the
     * scrolling panel — and that same element is the container the chat itself renders
     * into. So the hide must be lifted before opening, or `open()` succeeds into a
     * `display:none` box and support is unreachable while appearing to work.
     *
     * Found by asserting BEHAVIOUR rather than presence: the entry rendered, the click
     * landed, `open()` was called, and the frame measured 0px at `display:none`. A spec
     * checking `typeof open === 'function'` would have shipped this.
     *
     * The class is ours and the CSS keys off it, so nothing here depends on the vendor.
     */
    document.body.classList.add(SUPPORT_OPEN_CLASS)
    openWithRetry(w)
    watchForClose()
    return true
  } catch (e) {
    console.error('[SAB-943] Freshdesk open() failed:', e)
    document.body.classList.remove(SUPPORT_OPEN_CLASS)
    return false
  }
}

/**
 * Call `open()` until the chat actually opens, or give up.
 *
 * ⚠️ THE VENDOR DROPS EARLY OPENS, SILENTLY. Measured: this fails roughly HALF the time on a
 * cold widget. `open()` posts `open_chat` into the iframe, and the iframe's chat app loads
 * lazily — its `src` is empty when the frame is first created. A message posted before that
 * app is listening is discarded, with no error and no retry, so the user taps support and
 * NOTHING HAPPENS. A single call looked correct in every fast run and failed 3 of 6 on a
 * cold one.
 *
 * That is precisely the failure this whole ticket exists to prevent: with the launcher
 * hidden in the room (SAB-942), a dropped open leaves no route to support at all during a
 * live draft. So the call is repeated until the frame reports `fd-open`, and bounded so a
 * genuinely broken widget does not spin forever.
 */
function openWithRetry(w: FdWidget, attempts = 12, everyMs = 900): void {
  let n = 0
  const tryOpen = () => {
    const frame = document.getElementById(FRAME_ID)
    if (frame?.classList.contains(FD_OPEN_CLASS)) return          // it took
    if (n++ >= attempts) {
      console.error('[SAB-943] Freshdesk open() never took after', attempts, 'attempts')
      return
    }
    try { w.open?.() } catch (e) { console.error('[SAB-943] open() threw:', e) }
    window.setTimeout(tryOpen, everyMs)
  }
  tryOpen()
}

/**
 * Drop the class again once the user closes the chat, so the launcher goes back to being
 * hidden in the room rather than sitting over the board for the rest of the draft.
 *
 * Polled on `isOpened()` rather than bound to a vendor event: `fdWidget.on()` exists, but
 * which event name it emits on close is decided inside the iframe and is not readable from
 * the loader — the same class of assumption SAB-916 forbids. A bounded poll on a documented
 * state getter is the weaker mechanism and the one we can actually verify.
 */
function watchForClose(): void {
  /**
   * Keyed on the frame's own `fd-open` class, NOT on `isOpened()`.
   *
   * The first version polled `isOpened()` and removed the class within 500ms every time,
   * because that getter reads false even while the chat is demonstrably open — so it
   * re-hid the frame immediately and support never appeared. Self-defeating, and only
   * visible because the spec asserted the RESULT rather than the call.
   *
   * Two phases: wait for the chat to actually open (bounded, so a failed open still
   * releases the class), then wait for it to close again.
   */
  let opened = false
  let ticks = 0
  const id = window.setInterval(() => {
    const frame = document.getElementById(FRAME_ID)
    const isOpen = !!frame?.classList.contains(FD_OPEN_CLASS)

    if (isOpen) { opened = true; ticks = 0; return }
    // Closed AFTER having been open -> the user dismissed it. Re-hide.
    if (opened) { done(); return }
    // Never opened within ~20s -> release anyway rather than leaving the hide lifted.
    if (++ticks > 40) done()
  }, 500)

  function done() {
    document.body.classList.remove(SUPPORT_OPEN_CLASS)
    window.clearInterval(id)
  }
}

/**
 * Whether an in-room support control should RENDER at all.
 *
 * Deliberately not the same thing as `useWidgetAllowed()`. Consent can be given while the
 * vendor script is still in flight, and a control shown in that window would do nothing when
 * tapped — SAB-790's dead commissioner buttons, rebuilt. So this polls for the widget
 * actually being loaded and the control stays ABSENT until then.
 */
export function useSupportAvailable(): boolean {
  const allowed = useWidgetAllowed()
  const ready = () => !!document.getElementById(FRAME_ID) && typeof window.fdWidget?.open === 'function'
  const [loaded, setLoaded] = useState(ready)

  useEffect(() => {
    if (!allowed) { setLoaded(false); return }
    if (ready()) { setLoaded(true); return }
    /**
     * Polls for the vendor's FRAME, not for `isLoaded()`. That getter reads false while the
     * widget works (measured), so gating on it would leave the entry absent on exactly the
     * runs where support is available. The frame element is what `open()` needs, which makes
     * it both the honest readiness signal and the same condition openSupport() checks.
     *
     * There is no ready event to bind to, so a bounded poll is the mechanism; it gives up
     * rather than running forever.
     */
    let tries = 0
    const id = window.setInterval(() => {
      if (ready()) { setLoaded(true); window.clearInterval(id) }
      else if (++tries > 60) window.clearInterval(id)   // ~30s
    }, 500)
    return () => window.clearInterval(id)
  }, [allowed])

  return allowed && loaded
}

/** Reactive form of `widgetAllowed()`, so a consent change takes effect without a reload. */
export function useWidgetAllowed(): boolean {
  const [allowed, setAllowed] = useState(widgetAllowed)
  useEffect(() => subscribeConsent(() => setAllowed(widgetAllowed())), [])
  return allowed
}
